import { Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { isDataClientError } from '@kk/domain';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useNavigate } from 'react-router';

import { pendingInvite } from '@/auth/pendingInvite';
import { useClients, useSession } from '@/auth/SessionProvider';
import { deck } from '@/copy/deck';
import { useErrorMessage } from '@/data/hooks';
import { isAdminHost } from '@/lib/host';
import { NotOnAClub } from '@/screens/NotOnAClub';
import { NotStaffHere } from '@/screens/NotStaffHere';
import { SignInShell } from '@/screens/SignInShell';

const RESEND_SECONDS = 60;
const reasonOf = (e: unknown): unknown =>
  isDataClientError(e) && typeof e.details === 'object' && e.details !== null
    ? (e.details as { reason?: unknown }).reason
    : undefined;

/**
 * **OUR OWN SIGN-IN, EMAIL FIRST** (board O00, O00b, O00c, O00d; Evan 2026-09-26). One field and Continue. A company
 * address goes straight to Google; any other address is mailed a code and this card becomes O00d. The clubs portal
 * keeps "Or continue with Google" under Continue; the admin has no Google button. No passwords on either site.
 */
export function EmailSignIn({ ways }: { ways: ReactNode }) {
  const { auth } = useClients();
  const { problem } = useSession();
  const errorMessage = useErrorMessage();
  const admin = isAdminHost();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(
    problem === 'notCompanyAccount' ? deck.signIn.notCompanyAccount : null,
  );
  const [codeFor, setCodeFor] = useState<{ email: string; requestedAt: number } | null>(null);
  const [refused, setRefused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (admin) document.title = deck.signIn.admin.tabTitle;
  }, [admin]);
  useEffect(() => {
    void auth.lastEmail().then((e) => {
      if (e) setEmail((current) => current || e);
    });
  }, [auth]);

  if (refused || problem === 'notStaff') return admin ? <NotStaffHere /> : <NotOnAClub />;

  const google = async (opts: { email?: string; companyOnly: boolean }) => {
    setBusy(true);
    setError(null);
    try {
      await auth.beginGoogle?.(opts);
      // it leaves the page: `busy` stays set so the button cannot spring back under a navigating browser
    } catch (e) {
      console.error('[kk portal] Google sign-in could not start:', e);
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const proceed = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const started = await auth.startSignIn!(email);
      if (started.method === 'google') return void (await google({ email, companyOnly: true }));
      setCodeFor({ email: email.trim(), requestedAt: Date.now() });
    } catch (e) {
      setError(errorMessage(e));
    }
    setBusy(false);
  };

  if (codeFor)
    return (
      <CodeStep
        email={codeFor.email}
        requestedAt={codeFor.requestedAt}
        onResent={(at) => setCodeFor({ ...codeFor, requestedAt: at })}
        onRefused={() => {
          // a first-time director is staff of nothing UNTIL their invite is accepted: finish what they came to do
          if (pendingInvite.read()) navigate('/invites/accept', { replace: true });
          else setRefused(true);
        }}
        onDifferentEmail={() => {
          setCodeFor(null);
          setEmail(''); // O00d: "Use a different email goes back with the field cleared"
        }}
      />
    );

  return (
    <SignInShell>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void proceed();
        }}
      >
        <Text variant="chip" tone="slate" testID="signin-eyebrow">
          {admin ? deck.signIn.admin.eyebrow : deck.signIn.eyebrow}
        </Text>
        <Title2 tone="ink">{admin ? deck.signIn.admin.heading : deck.signIn.heading}</Title2>
        <Text variant="bodySm" tone="slate">
          {admin ? deck.signIn.admin.body : deck.signIn.body}
        </Text>
        <TextField
          label={deck.signIn.emailAddress}
          value={email}
          onChangeText={setEmail}
          autoComplete="email"
          autoCapitalize="none"
          keyboardType="email-address"
          testID="email"
        />
        {error ? (
          <Text variant="helper" tone="error" testID="sign-in-error">
            {error}
          </Text>
        ) : null}
        <WasabiButton label={deck.signIn.continue} onPress={() => void proceed()} loading={busy} testID="sign-in" />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        {!admin && auth.googleAvailable ? (
          <WasabiButton
            label={deck.signIn.orGoogle}
            variant="text"
            textSize="sm"
            onPress={() => void google({ companyOnly: false })}
            testID="sign-in-google"
          />
        ) : null}
      </form>
      {admin ? null : ways}
    </SignInShell>
  );
}

/**
 * **O00d · the email code, either site.** Six boxes, and pasting a code fills all six. It works for 15 minutes; five
 * wrong tries void it; "Send a new code" appears after 60 seconds. A right code signs the person in and proves the
 * address, so an invite waiting for it joins at once.
 */
function CodeStep(props: {
  email: string;
  requestedAt: number;
  onResent: (at: number) => void;
  onRefused: () => void;
  onDifferentEmail: () => void;
}) {
  const { auth } = useClients();
  const errorMessage = useErrorMessage();
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const boxes = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => boxes.current[0]?.focus(), []);

  const wait = Math.max(0, RESEND_SECONDS - Math.floor((now - props.requestedAt) / 1000));
  const code = digits.join('');

  const fill = (from: number, text: string) => {
    const typed = text.replace(/\D/g, '');
    if (!typed) {
      setDigits((d) => d.map((x, i) => (i === from ? '' : x)));
      return;
    }
    setDigits((d) => {
      const next = [...d];
      for (let i = 0; i < typed.length && from + i < 6; i += 1) next[from + i] = typed[i]!;
      return next;
    });
    boxes.current[Math.min(from + typed.length, 5)]?.focus();
  };

  const submit = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await auth.verifyCode!(props.email, code);
      return; // the session arrives through the provider and the route moves on
    } catch (e) {
      if (reasonOf(e) === 'notStaff') return props.onRefused();
      const dead =
        isDataClientError(e) &&
        (e.code === 'INVALID_STATE' || (e.code === 'RATE_LIMITED' && reasonOf(e) === 'codeVoided'));
      setError(
        dead
          ? deck.signIn.code.dead
          : isDataClientError(e) && e.code === 'INVALID_CREDENTIALS'
            ? deck.signIn.code.wrong
            : errorMessage(e),
      );
      setDigits(['', '', '', '', '', '']);
      boxes.current[0]?.focus();
    }
    setBusy(false);
  };

  const resend = async () => {
    setError(null);
    try {
      await auth.startSignIn!(props.email);
      props.onResent(Date.now());
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <SignInShell>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        data-testid="code-step"
      >
        <Title2 tone="ink">{deck.signIn.code.title}</Title2>
        <Text variant="bodySm" tone="slate">
          {deck.signIn.code.sentTo} <strong data-testid="code-email">{props.email}</strong>
        </Text>
        <div className="code-boxes" role="group" aria-label={deck.signIn.code.title}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                boxes.current[i] = el;
              }}
              className="code-box"
              value={d}
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={i === 0 ? 6 : 1}
              aria-label={`${i + 1}`}
              data-testid={`code-${i}`}
              onChange={(e) => fill(i, e.target.value)}
              onPaste={(e) => {
                e.preventDefault();
                fill(0, e.clipboardData.getData('text'));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && !digits[i] && i > 0) boxes.current[i - 1]?.focus();
              }}
            />
          ))}
        </div>
        {error ? (
          <Text variant="helper" tone="error" testID="code-error">
            {error}
          </Text>
        ) : null}
        <WasabiButton
          label={deck.signIn.code.cta}
          onPress={() => void submit()}
          loading={busy}
          disabled={code.length !== 6}
          testID="code-sign-in"
        />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        <WasabiButton
          label={deck.signIn.code.differentEmail}
          variant="text"
          textSize="sm"
          onPress={props.onDifferentEmail}
          testID="different-email"
        />
        {wait > 0 ? (
          <Text variant="bodySm" tone="slate" testID="resend-wait">
            {deck.signIn.code.resendIn(wait)}
          </Text>
        ) : (
          <WasabiButton
            label={deck.signIn.code.resend}
            variant="text"
            textSize="sm"
            onPress={() => void resend()}
            testID="resend"
          />
        )}
      </form>
    </SignInShell>
  );
}
